import type { Participant } from '@seen/core/models/patients/participant'
import { RecentSelections } from '@seen/core/models/recentSelections'
import { Badge } from '@seen/ui'
import { IconPhone } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type JSX, useCallback } from 'react'
import { CommandMenuLink } from '#src/components/app/command-menu/CommandMenuLink.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { PatientName } from '#src/components/patient/PatientName.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export interface PatientItem {
  type: 'participant'
  value: string
  participant: Participant
}

export function PatientCommandLink({
  data: { value, participant },
  highlightedInitials,
}: {
  data: PatientItem
  highlightedInitials?: string
}): JSX.Element {
  const currentPractitioner = useCurrentPractitioner()
  const { data: recentSelections } = useRead(
    RecentSelections,
    currentPractitioner?.recentSelections
  )
  const patchRecentSelections = usePatchMutation<RecentSelections>(
    currentPractitioner?.recentSelections?.reference
      ? {
          mutation: {
            scope: {
              id: currentPractitioner.recentSelections.reference,
            },
          },
        }
      : undefined
  ).mutate
  const handleSelect = useCallback(() => {
    if (!recentSelections) {
      return
    }
    patchRecentSelections({
      instance: recentSelections,
      mutateFn: (rs) => {
        rs.add(value)
      },
    })
  }, [recentSelections, patchRecentSelections, value])
  return (
    <CommandMenuLink
      value={value}
      to={participant.relativeUrl}
      className="flex gap-3"
      onSelect={handleSelect}
      data-testid="patient-command-result"
    >
      <Avatar size="icon" profile={participant.reference} />
      <div className="flex flex-1 items-center justify-between gap-1">
        <div className="flex items-center gap-2">
          {highlightedInitials && (
            <NameWithHighlightedInitials
              participant={participant}
              highlightedInitials={highlightedInitials}
              className="line-clamp-1"
            />
          )}
          {!highlightedInitials && (
            <PatientName participant={participant} className="line-clamp-1" />
          )}
          {participant.isLead && <Badge variant="outline">Lead</Badge>}
          {participant.isDisenrolled && (
            <Badge variant="outline" color="red">
              Disenrolled
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {participant.birthDate && (
            <Badge variant="outline">{participant.birthDateFormatted}</Badge>
          )}
          {participant.primaryPhoneNumber && (
            <Badge variant="outline">
              <IconPhone className="size-2" />
              {participant.primaryPhoneNumber}
            </Badge>
          )}
          {participant.seenMRN && (
            <Badge variant="outline">MRN {participant.seenMRN}</Badge>
          )}
        </div>
      </div>
    </CommandMenuLink>
  )
}

function NameWithHighlightedInitials({
  participant,
  highlightedInitials,
  className,
}: {
  participant: Participant
  highlightedInitials: string
  className?: string
}): JSX.Element {
  const nameParts = participant.name.split(' ')
  const alternativeNames = compact([participant.nickname]).join(', ')
  return (
    <span className={className}>
      {nameParts.map((n, idx) => (
        <span key={idx}>
          <span
            className={
              n[0] &&
              highlightedInitials.toLowerCase().includes(n[0].toLowerCase())
                ? 'font-bold'
                : ''
            }
          >
            {n[0]}
          </span>
          {n.slice(1)}
          {idx < nameParts.length - 1 && ' '}
        </span>
      ))}
      {alternativeNames && (
        <span className="text-foreground-secondary"> ({alternativeNames})</span>
      )}
    </span>
  )
}
